import {
	Box,
	Card,
	Center,
	Divider,
	Flex,
	Group,
	Loader,
	Stack,
	createStyles,
} from '@mantine/core';
import type { ScoreBreakdown } from '@repo/api-codegen';
import { useApiGetScoreHistoryForWorkspace } from '@repo/api-codegen';
import { Banner, Button, Text } from '@repo/foundations';
import { useMount } from 'ahooks';
import { useCallback, useMemo, useState } from 'react';
import { DataQualityBenchmarkTooltip } from '../../../../components/DataQualityScore/DataQualityBenchmarkTooltip/DataQualityBenchmarkTooltip';
import { QualityScoreRing } from '../../../../components/DataQualityScore/QualityScoreRing/QualityScoreRing';

import { trackEvent } from '@repo/common/analytics';
import { HoverCard } from '@repo/common/components/HoverCard';
import { useAuthUser } from '../../../../api';
import { useSuspenseFeatureAccess } from '../../../../api/hooks/workspace/useSuspenseFeatureAccess';

import { DataQualityTimeseriesChartLoading } from '../../../../components/DataQualityScore/DataQualityTimeseriesChart/DataQualityTimeseriesChartLoading';
import { DataQualityTimeseriesChartV2 } from '../../../../components/DataQualityScore/DataQualityTimeseriesChart/DataQualityTimeseriesChartV2';
import { QualityScorePercentage } from '../../../../components/DataQualityScore/QualityScoreRing/QualityScorePercentage';
import { QualityScoreRingLoading } from '../../../../components/DataQualityScore/QualityScoreRing/QualityScoreRingLoading';
import { EmptyState } from '../../../../components/EmptyState';
import { IntegrationSelector } from '../../../../components/EntityModal/Metadata/IntegrationSelector';
import { TagSelector } from '../../../../components/EntityModal/Metadata/TagSelector';
import { UpgradeButton } from '../../../../components/Settings/UpgradeButton';
import { TeamAndGroupSelector } from '../../components/TeamAndGroupSelector/TeamAndGroupSelector';

const useStyles = createStyles((theme) => ({
	tooltipTarget: {
		borderBottom: `dotted 1px ${theme.other.getColor('border/secondary/default')}`,
	},
	ringCard: {
		overflow: 'visible',
	},
	timeseriesCard: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		border: `solid 1px ${theme.other.getColor('border/secondary/default')}`,
		borderRadius: theme.radius.md,
	},
	timeseries: {
		padding: `${theme.spacing.md} ${theme.spacing.md}`,
	},
}));

// Toggle when ready to re-enable filtering controls.
const SHOW_FILTERS = true;

type FilterType = 'integration' | 'team' | 'tag' | null;

type PolicyBreakdownDetail = ScoreBreakdown & {
	policy: {
		id: string;
		name: string;
		enabled: boolean;
	};
	entity_count: number;
	passing_entities: number;
	failing_entities: number;
};

export function DataQualityReportV2() {
	const { classes } = useStyles();
	const { user, workspace } = useAuthUser();

	const [teamId, setTeamId] = useState<string>('');
	const [integrationId, setIntegrationId] = useState<string>('');
	const [tagId, setTagId] = useState<string>('');
	const [activeFilterType, setActiveFilterType] = useState<FilterType>(null);
	const { dqsAccess } = useSuspenseFeatureAccess();

	const handleClearFilters = useCallback(() => {
		setTeamId('');
		setIntegrationId('');
		setTagId('');
		setActiveFilterType(null);
	}, []);

	const scoreHistoryQueryParams = useMemo(() => {
		if (teamId) {
			return { team_id: teamId };
		}
		if (integrationId) {
			return { integration_id: integrationId };
		}
		if (tagId) {
			return { tag_id: tagId };
		}
		return {};
	}, [integrationId, teamId, tagId]);

	useMount(() => {
		trackEvent('dqs/report/open', {}, user, workspace);
	});

	const { data: scoreHistory, isLoading: isLoadingHistory } =
		useApiGetScoreHistoryForWorkspace({
			queryParams: scoreHistoryQueryParams,
		});

	const filteredScoreHistoryPoints = useMemo(() => {
		if (!scoreHistory?.points) {
			return [];
		}

		return scoreHistory.points.filter((point) => {
			const hasPossibleScore = point.total_possible > 0;
			const hasPolicyBreakdown =
				Object.keys(point.policy_breakdown ?? {}).length > 0;
			return hasPossibleScore || hasPolicyBreakdown;
		});
	}, [scoreHistory?.points]);

	// Get the latest point from score_history for current breakdown
	const latestScorePoint = useMemo(() => {
		if (!filteredScoreHistoryPoints.length) {
			return null;
		}
		return filteredScoreHistoryPoints[filteredScoreHistoryPoints.length - 1];
	}, [filteredScoreHistoryPoints]);

	// Convert latest score point to breakdown format
	const currentBreakdown = useMemo(() => {
		if (!latestScorePoint) {
			return null;
		}

		const policyBreakdowns: PolicyBreakdownDetail[] = Object.values(
			latestScorePoint.policy_breakdown ?? {}
		).map((policyData) => ({
			policy: {
				id: policyData.policy_id,
				name: policyData.policy_name,
				enabled: true,
			},
			percentage: policyData.percentage,
			score: policyData.score,
			possible: policyData.possible,
			policy_id: policyData.policy_id,
			policy_name: policyData.policy_name,
			entity_count: 0,
			passing_entities: 0,
			failing_entities: 0,
		}));

		const overallPercentage =
			latestScorePoint.total_possible > 0
				? (latestScorePoint.total_score / latestScorePoint.total_possible) * 100
				: 0;

		return {
			percentage: overallPercentage,
			policies: policyBreakdowns,
		};
	}, [latestScorePoint]);

	// Check if there are no policies with data
	const allPoliciesDisabled = useMemo(
		() => !currentBreakdown?.policies || currentBreakdown.policies.length === 0,
		[currentBreakdown?.policies]
	);

	// Get the filter name for the banner message
	const getFilterName = useCallback(() => {
		if (activeFilterType === 'team') {
			return 'the selected team';
		}
		if (activeFilterType === 'integration') {
			return 'the selected integration';
		}
		if (activeFilterType === 'tag') {
			return 'the selected tag';
		}
		return null;
	}, [activeFilterType]);

	const hasActiveFilter = activeFilterType !== null;
	const showNoDataBanner = allPoliciesDisabled && hasActiveFilter;

	if (!dqsAccess) {
		return (
			<EmptyState
				title="Upgrade to access Quality Score"
				description="You can use the Data Quality Score to understand and improve your data quality."
				illustrationName="upgrade"
				includeGoBack={false}
				stateHeight="50vh"
				size="lg"
				withActions={<UpgradeButton feature="DQS" size="md" />}
			/>
		);
	}

	if (!workspace.quality_enabled) {
		return (
			<EmptyState
				illustrationName="upgrade"
				title="Quality Score is disabled"
				description="Quality Score is not enabled for this workspace. Please contact your administrator to turn on access."
				includeGoBack={false}
				stateHeight="80vh"
				size="lg"
			/>
		);
	}

	return (
		<Stack spacing="md" w="100%" pb="md">
			{SHOW_FILTERS && (
				<Group spacing="xs" align="center">
					{!activeFilterType && (
						<>
							<TeamAndGroupSelector
								includeGroups={false}
								value={teamId}
								onGroupSelected={() => {}}
								onTeamSelected={(value) => {
									setTeamId(value);
									if (value) {
										setActiveFilterType('team');
									}
								}}
							/>
							<IntegrationSelector
								onChange={(value) => {
									setIntegrationId(value);
									if (value) {
										setActiveFilterType('integration');
									}
								}}
								initialValue={integrationId}
								allowEmptyOption
							/>
							<TagSelector
								onChange={(value) => {
									setTagId(value);
									if (value) {
										setActiveFilterType('tag');
									}
								}}
								initialValue={tagId}
								allowEmptyOption
							/>
						</>
					)}
					{activeFilterType === 'team' && (
						<TeamAndGroupSelector
							includeGroups={false}
							value={teamId}
							onGroupSelected={() => {}}
							onTeamSelected={(value) => {
								setTeamId(value);
								if (!value) {
									setActiveFilterType(null);
								}
							}}
						/>
					)}
					{activeFilterType === 'integration' && (
						<IntegrationSelector
							onChange={(value) => {
								setIntegrationId(value);
								if (!value) {
									setActiveFilterType(null);
								}
							}}
							initialValue={integrationId}
							allowEmptyOption
						/>
					)}
					{activeFilterType === 'tag' && (
						<TagSelector
							onChange={(value) => {
								setTagId(value);
								if (!value) {
									setActiveFilterType(null);
								}
							}}
							initialValue={tagId}
							allowEmptyOption
						/>
					)}
					{activeFilterType && (
						<Button variant="tertiary" size="md" onClick={handleClearFilters}>
							Clear filter
						</Button>
					)}
				</Group>
			)}
			{showNoDataBanner && (
				<Banner
					tone="info"
					message={`No data quality scores were found for ${getFilterName()}.`}
				/>
			)}
			{!showNoDataBanner && (
				<>
					<Group position="apart" noWrap>
						<Card withBorder w={228} className={classes.ringCard}>
							<Group>
								{isLoadingHistory && <QualityScoreRingLoading size="xs" />}
								{!isLoadingHistory && currentBreakdown && (
									<QualityScoreRing
										score={
											allPoliciesDisabled ? 0 : currentBreakdown.percentage
										}
										size="xs"
										borderWidth="sm"
										showInnerContent={false}
									/>
								)}
								<HoverCard
									target={
										<Stack spacing={0}>
											<Text
												color="text/secondary/default"
												align="center"
												size="sm"
												pb="xs"
											>
												Overall quality
											</Text>
											{currentBreakdown && (
												<QualityScorePercentage
													size="2xs"
													score={
														allPoliciesDisabled
															? undefined
															: currentBreakdown.percentage
													}
													displayBadge={!allPoliciesDisabled}
													showUnavailableBadge={allPoliciesDisabled}
												/>
											)}
										</Stack>
									}
								>
									<DataQualityBenchmarkTooltip />
								</HoverCard>
							</Group>
						</Card>
						<Card withBorder>
							<Group>
								{isLoadingHistory && (
									<Center w="100%" p="md">
										<Loader size="sm" />
									</Center>
								)}
								{currentBreakdown && currentBreakdown.policies.length === 0 && (
									<Center w="100%" p="md">
										<Text color="text/secondary/default" size="sm">
											No policies configured
										</Text>
									</Center>
								)}
								{currentBreakdown?.policies.map(
									(policyData: PolicyBreakdownDetail, index: number) => (
										<>
											<Box key={policyData.policy.id} w={108} pl="xs">
												<Text color="text/secondary/default" size="sm" pb="xs">
													{policyData.policy.name}
												</Text>
												<Group spacing="xs">
													<QualityScoreRing
														score={policyData.percentage}
														size="2xs"
														borderWidth="sm"
														showInnerContent={false}
													/>
													<QualityScorePercentage
														size="2xs"
														score={policyData.percentage}
														displayBadge={false}
													/>
												</Group>
											</Box>
											{index < currentBreakdown.policies.length - 1 && (
												<Divider orientation="vertical" />
											)}
										</>
									)
								)}
							</Group>
						</Card>
					</Group>
					<Flex style={{ flex: 1 }}>
						<Stack spacing={0} className={classes.timeseriesCard}>
							<Text size="sm" p="sm" fw="bold">
								Quality over time
							</Text>
							<Box className={classes.timeseries}>
								{isLoadingHistory && (
									<DataQualityTimeseriesChartLoading
										width="100%"
										height={249}
									/>
								)}
								{!!filteredScoreHistoryPoints.length && (
									<DataQualityTimeseriesChartV2
										width="100%"
										height={249}
										scoreHistory={filteredScoreHistoryPoints}
									/>
								)}
								{!isLoadingHistory && !filteredScoreHistoryPoints.length && (
									<Center w="100%" h={249}>
										<Text color="text/secondary/default" size="sm">
											No historical quality data yet
										</Text>
									</Center>
								)}
							</Box>
						</Stack>
					</Flex>
				</>
			)}
		</Stack>
	);
}
